import React, { useMemo, useState } from 'react';

import { compareSortKeys } from '@aline/common';
import { Autocomplete, TextField, Typography, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineCheckbox, FilterTags } from 'src/components';
import type { Apollo } from 'src/gql/generated';
import { Apollo as ApolloOps } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { filterAutocompleteStyle } from './autocomplete-filter-utils';
import { ProgressiveColumnIcon } from '../../workflow/board/settings/ColumnItem';

const useStyles = makeStyles()(() => ({
  autocomplete: filterAutocompleteStyle,
  item: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
}));

interface ColumnFilterOption {
  __typename: 'Column';
  boardName: string;
  category: Apollo.ColumnCategory;
  color: string;
  id: string;
  /** 1-based position within the board's Started category (1 when not Started) */
  index: number;
  name: string;
  /** Total columns in the board's Started category (1 when not Started) */
  total: number;
}

export function ColumnAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const { data } = ApolloOps.useGetBoardsQuery();
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<ColumnFilterOption[]>(item.value ?? []);

  const options = useMemo(() => {
    const boards = data?.boards ?? [];
    return boards
      .flatMap(board => {
        const startedColumns = (board.columns ?? [])
          .filter(c => c.category === ApolloOps.ColumnCategory.Started)
          .sort((a, b) => compareSortKeys(a.sortKey, b.sortKey));
        return (board.columns ?? []).map(col => {
          const startedIdx = startedColumns.findIndex(c => c.id === col.id);
          return {
            __typename: 'Column' as const,
            boardName: board.name,
            category: col.category,
            color: col.color,
            id: col.id,
            index: startedIdx >= 0 ? startedIdx + 1 : 1,
            name: col.name,
            total: startedColumns.length || 1,
          };
        });
      })
      .sort((a, b) => a.boardName.localeCompare(b.boardName));
  }, [data?.boards]);

  function handleValueChange(newValue: ColumnFilterOption[]): void {
    applyValue({ ...item, value: newValue });
    setValue(newValue);
  }

  return (
    <Autocomplete<ColumnFilterOption, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionLabel={option => option.name}
      groupBy={option => option.boardName}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      options={options}
      renderInput={params => (
        <TextField placeholder="Search statuses..." {...params} variant="standard" />
      )}
      renderOption={(props, option, state) => {
        const { className, ...otherProps } = props;
        return (
          <li {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <ProgressiveColumnIcon
              category={option.category}
              color={option.color}
              iconSize={16}
              index={option.index}
              total={option.total}
            />
            <Typography>{option.name}</Typography>
          </li>
        );
      }}
      renderValue={tags => (
        <FilterTags<ColumnFilterOption>
          getTextFromTag={tag => tag.name}
          tags={tags}
          truncateTags={truncateTags}
          onDelete={tag => handleValueChange(tags.filter(t => t.id !== tag.id))}
        />
      )}
      slotProps={{
        popper: {
          modifiers: [
            { enabled: false, name: 'flip' },
            { enabled: false, name: 'preventOverflow' },
          ],
        },
      }}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, newValue) => handleValueChange(newValue)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
    />
  );
}
